import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePlatform } from '../contexts/PlatformContext';
import { useAuth } from '../contexts/AuthContext';
import { Badge } from '../components/common/Badge';
import { Pagination, usePaginationState } from '../components/common/Pagination';
import PageHeader from '../components/common/PageHeader';
import PageContainer from '../components/common/PageContainer';
import { RotateCw, ArrowRight, Zap, ShieldCheck, Activity, Clock, Sparkles, Globe, Shield } from 'lucide-react';
import { AGENT_TYPES, AGENT_TYPE_ORDER, classifyAgentType } from '../constants/agentTypes';

// ---------------------------------------------------------------------------
// Phase display map
// ---------------------------------------------------------------------------
const PHASE_LABELS = {
  research: 'Build',  // normalize legacy "research" → Build
  build: 'Build',
  decide: 'Decide',
  act: 'Act',
};

const PHASE_COLORS = {
  build: { bg: '#f5f5f4', text: '#57534e', accent: '#78716c' },
  decide: { bg: '#e3f4f1', text: '#085041', accent: '#d97706' },
  act: { bg: '#e3f4f1', text: '#9a2f23', accent: '#0d9488' },
};

// Brand palette
const BRAND = {
  primary: '#0d9488',
  dark: '#0a766b',
  light: '#e3f4f1',
  glow: 'rgba(226, 74, 58, 0.25)',
};

const formatPhase = (phase) => {
  if (phase == null) return 'Unknown';
  if (typeof phase === 'string') return PHASE_LABELS[phase.toLowerCase()] || phase;
  if (typeof phase === 'number') {
    if (phase === 1) return 'Build';
    if (phase === 2) return 'Decide';
    return 'Act';
  }
  return String(phase);
};

const VALID_PHASES = ['build', 'decide', 'act'];
const normalizePhase = (p) => {
  const lower = (p || 'build').toLowerCase();
  return VALID_PHASES.includes(lower) ? lower : 'build';
};

// ---------------------------------------------------------------------------
// Auto-refresh interval (ms)
// ---------------------------------------------------------------------------
const REFRESH_INTERVAL_MS = 30_000;

// ---------------------------------------------------------------------------
// Metric icon map
// ---------------------------------------------------------------------------
const METRIC_CONFIG = [
  { key: 'agents', label: 'Total Agents', icon: Zap, color: '#0d9488', bg: '#e3f4f1' },
  { key: 'skills', label: 'Total Skills', icon: ShieldCheck, color: '#0a766b', bg: '#e3f4f1' },
  { key: 'executions', label: 'Active Executions', icon: Activity, color: '#78716c', bg: '#f5f5f4' },
  { key: 'approvals', label: 'Pending Approvals', icon: Clock, color: '#a8a29e', bg: '#fafaf9' },
];

// ---------------------------------------------------------------------------
// Agent buckets — each gets its own outline + gradient so the Overview
// reads as four distinct fleets at a glance. Colours and labels live in
// /constants/agentTypes.js so a tweak to a hex value or a label
// rename lands in exactly one place. The "All" bucket is local to this
// page (not a real type) so it stays defined inline.
// ---------------------------------------------------------------------------
const AGENT_BUCKETS = {
  all: {
    key: 'all',  label: 'All',
    icon: Zap,   accent: '#52525b',
    border: '#e7e5e4',
    bg: 'linear-gradient(180deg, #ffffff 0%, #fafaf9 100%)',
    description: 'Full fleet across every bucket',
  },
  autolets: {
    key: 'autolets', label: AGENT_TYPES.autolets.label,
    icon: Sparkles, accent: AGENT_TYPES.autolets.accent,
    border: AGENT_TYPES.autolets.border,
    bg: AGENT_TYPES.autolets.bgGradient,
    description: AGENT_TYPES.autolets.description,
  },
  agentlet: {
    key: 'agentlet', label: AGENT_TYPES.agentlet.label,
    icon: Zap, accent: AGENT_TYPES.agentlet.accent,
    border: AGENT_TYPES.agentlet.border,
    bg: AGENT_TYPES.agentlet.bgGradient,
    description: AGENT_TYPES.agentlet.description,
  },
  external: {
    key: 'external', label: AGENT_TYPES.external.label,
    icon: Globe, accent: AGENT_TYPES.external.accent,
    border: AGENT_TYPES.external.border,
    bg: AGENT_TYPES.external.bgGradient,
    description: AGENT_TYPES.external.description,
  },
  system: {
    key: 'system', label: AGENT_TYPES.system.label,
    icon: Shield, accent: AGENT_TYPES.system.accent,
    border: AGENT_TYPES.system.border,
    bg: AGENT_TYPES.system.bgGradient,
    description: AGENT_TYPES.system.description,
  },
};
const BUCKET_ORDER = ['all', ...AGENT_TYPE_ORDER];

// Re-export the shared classifier for everywhere on this page that
// needs it — keeps the single-source-of-truth invariant.
const classifyAgent = classifyAgentType;

export default function OverviewPage() {
  const { agents: rawAgents, dashboardMetrics, loadAgents, loadDashboardMetrics, loading, error } =
    usePlatform();
  const { session, currentRole } = useAuth();

  // RBAC: hide system agents from non-Super Admin users
  const agents = currentRole === 'Super Admin'
    ? rawAgents
    : rawAgents.filter(a => !a.isSystemAgent && a.category !== 'system');
  const navigate = useNavigate();

  const [lastRefreshed, setLastRefreshed] = useState(null);
  const intervalRef = useRef(null);
  const pgAgents = usePaginationState(10);

  // Selected agent bucket (one of: 'all' | 'autolets' | 'agentlet' |
  // 'external' | 'system'). Persisted across page navigations so a user
  // who lives in the AutoLets tab doesn't have to re-pick on every visit.
  const [bucket, setBucket] = useState(() => {
    try { return sessionStorage.getItem('agentlets_overview_bucket') || 'all'; }
    catch (_) { return 'all'; }
  });
  useEffect(() => {
    try { sessionStorage.setItem('agentlets_overview_bucket', bucket); } catch (_) {}
    pgAgents.resetPage();
    // pgAgents.resetPage identity is stable; re-running on every render
    // is harmless. The exhaustive-deps lint suppression that was here
    // was removed 2026-05-04 — that rule plugin isn't configured in
    // this project, so naming it in a disable directive trips the
    // build under CRA's stricter ESLint defaults.
  }, [bucket]);

  const orgId = session?.orgId;
  const tenantId = session?.tenantId;

  const refresh = useCallback(() => {
    const scope = { orgId, tenantId };
    loadAgents(scope);
    loadDashboardMetrics(scope);
    setLastRefreshed(new Date());
  }, [orgId, tenantId, loadAgents, loadDashboardMetrics]);

  useEffect(() => {
    refresh();
    intervalRef.current = setInterval(refresh, REFRESH_INTERVAL_MS);
    return () => clearInterval(intervalRef.current);
  }, [refresh]);

  // Metrics
  const totalAgents = dashboardMetrics?.totalAgents ?? agents.length;
  const totalSkills =
    dashboardMetrics?.totalSkills ??
    agents.reduce((acc, a) => acc + (a.skills?.length || 0), 0);
  const activeExecutions = dashboardMetrics?.activeExecutions ?? 0;
  const pendingApprovals = dashboardMetrics?.pendingApprovals ?? 0;
  const metricValues = [totalAgents, totalSkills, activeExecutions, pendingApprovals];

  return (
    <PageContainer>
      {/* ─── Header ─── */}
      {/* Standardised across the platform via the shared PageHeader so
          Overview / Agent Lifecycle / Trust Center / etc. all read as
          one product. Activity icon picked because Overview is the
          live-pulse view of the fleet. */}
      <PageHeader
        icon={Activity}
        title="Overview"
        subtitle="Platform status and key metrics"
        actions={
          <button
            onClick={refresh}
            disabled={loading}
            style={{
              padding: '9px 18px', borderRadius: 10,
              border: '1px solid #e7e5e4', background: '#ffffff',
              color: '#57534e', fontSize: 13, fontWeight: 500,
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1,
              display: 'flex', alignItems: 'center', gap: 7,
              fontFamily: 'inherit',
              boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
              transition: 'all 0.15s ease',
            }}
            title={lastRefreshed ? `Last refreshed: ${lastRefreshed.toLocaleTimeString()}` : 'Refresh now'}
          >
            <RotateCw size={14} style={loading ? { animation: 'spin 1s linear infinite' } : {}} />
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        }
      />

      {/* ─── Error ─── */}
      {error && (
        <div style={{
          background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 12,
          padding: '14px 18px', marginBottom: 20, color: '#991b1b', fontSize: 13,
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <strong>Error:</strong> {error}
          <button onClick={refresh} style={{
            marginLeft: 'auto', background: 'none', border: 'none',
            color: '#991b1b', textDecoration: 'underline', cursor: 'pointer', fontSize: 13,
          }}>
            Retry
          </button>
        </div>
      )}

      {/* ─── Loading ─── */}
      {loading && agents.length === 0 && (
        <div style={{
          textAlign: 'center', padding: 64, color: '#a8a29e',
          background: '#ffffff', borderRadius: 14, border: '1px solid #f5f5f4',
        }}>
          <RotateCw size={24} style={{ animation: 'spin 1s linear infinite', marginBottom: 14, color: '#d6d3d1' }} />
          <div style={{ fontSize: 14 }}>Loading agents...</div>
        </div>
      )}

      {/* ─── Empty State ─── */}
      {!loading && agents.length === 0 && !error && (
        <div style={{
          textAlign: 'center', padding: '64px 24px',
          background: '#ffffff', border: '1px dashed #e7e5e4',
          borderRadius: 14, marginBottom: 32,
        }}>
          <div style={{ fontSize: 48, marginBottom: 16, opacity: 0.4 }}>🤖</div>
          <h3 style={{ fontSize: 18, fontWeight: 650, color: '#1c1917', margin: '0 0 8px', letterSpacing: '-0.01em' }}>
            No agents yet
          </h3>
          <p style={{ fontSize: 14, color: '#78716c', margin: 0 }}>
            Create your first agent to get started. Agents will appear here once deployed.
          </p>
        </div>
      )}

      {/* ─── Operational Metrics ─────────────────────────────────────
          Restyled away from the fully-saturated red banner to a row of
          lightly-tinted cards using the Test & Verify gradient palette.
          Each metric keeps its own accent colour (primary / accent /
          stone / muted) to remain scannable but at far lower visual
          intensity than the previous hero banner. */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: 12, marginBottom: 28,
      }}>
        {METRIC_CONFIG.map((m, i) => {
          const Icon = m.icon;
          // Per-metric tinting — gentle gradient, primary tint only on
          // the agentlets-red ones, neutral on the others.
          const tints = {
            agents:     { bg: 'linear-gradient(180deg, #fef6f4 0%, #fff 75%)', border: '#a5d8d0', accent: '#0d9488' },
            skills:     { bg: 'linear-gradient(180deg, #fef6f4 0%, #fff 75%)', border: '#a5d8d0', accent: '#0a766b' },
            executions: { bg: 'linear-gradient(180deg, #f5f5f4 0%, #fff 75%)', border: '#e7e5e4', accent: '#52525b' },
            approvals:  { bg: 'linear-gradient(180deg, #fafaf9 0%, #fff 75%)', border: '#e7e5e4', accent: '#78716c' },
          };
          const t = tints[m.key];
          return (
            <div
              key={m.key}
              style={{
                background: t.bg, border: `1px solid ${t.border}`,
                borderRadius: 12, padding: '16px 18px',
                display: 'flex', alignItems: 'center', gap: 14,
                boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
              }}
            >
              <div style={{
                width: 38, height: 38, borderRadius: 10,
                background: '#fff', border: `1px solid ${t.border}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <Icon size={18} style={{ color: t.accent }} />
              </div>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 11, color: '#78716c', fontWeight: 500, letterSpacing: '0.02em', marginBottom: 4 }}>
                  {m.label}
                </div>
                <div style={{
                  fontSize: 26, fontWeight: 750, color: '#1c1917',
                  letterSpacing: '-0.02em', lineHeight: 1,
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                }}>
                  {metricValues[i]}
                  {m.key === 'executions' && metricValues[i] > 0 && (
                    <span style={{
                      width: 8, height: 8, borderRadius: '50%',
                      background: '#22c55e',
                      boxShadow: '0 0 6px rgba(34,197,94,0.5)',
                      animation: 'pulse 2s ease-in-out infinite',
                    }} />
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* ─── Agent Status Cards ─── */}
      {agents.length > 0 && (
        <>
          {/* Bucket counts — single pass over agents to drive the tab bar
              header counts AND the filter for the grid below. */}
          {(() => null)()}
          {/* (Bucket-count + filter logic computed inline in the tab bar
              callback below — keeping it co-located with the tab JSX so
              the file reads top-down without an aside helper.) */}

          {/* ─── Bucket tab bar ─── */}
          <BucketTabBar
            agents={agents}
            selected={bucket}
            onSelect={setBucket}
            lastRefreshed={lastRefreshed}
          />

          {/* Filtered list per selected bucket. */}
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
            gap: 16, marginBottom: 32,
          }}>
            {pgAgents.paginate(
              bucket === 'all'
                ? agents
                : agents.filter((a) => classifyAgent(a) === bucket)
            ).map((agent) => {
              const phaseKey = normalizePhase(agent.phase);
              const pc = PHASE_COLORS[phaseKey] || PHASE_COLORS.build;
              const bucketKey = classifyAgent(agent);
              const bucketCfg = AGENT_BUCKETS[bucketKey];
              return (
                <div
                  key={agent.id}
                  onClick={() => {
                    // Store navigation intent for AgentsPage to pick up on mount.
                    // Uses a dedicated key (not usePersistedState's internal key)
                    // so AgentsPage can detect and consume it as a one-time override.
                    sessionStorage.setItem('agentlets_nav_selectedAgent', String(agent.id));
                    navigate('/agents');
                  }}
                  style={{
                    background: bucketCfg.bg, border: `1px solid ${bucketCfg.border}`, borderRadius: 14,
                    padding: 0, cursor: 'pointer', overflow: 'hidden',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                    transition: 'box-shadow 0.2s ease, transform 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.08)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  {/* Bucket accent bar — colours mirror the bucket so the
                      card's bucket is identifiable even from the corner of
                      the eye. */}
                  <div style={{
                    height: 3, background: `linear-gradient(90deg, ${bucketCfg.accent} 0%, ${bucketCfg.accent}66 100%)`,
                  }} />

                  <div style={{ padding: '18px 20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14, gap: 8 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0, flex: 1, overflow: 'hidden' }}>
                        <div style={{
                          width: 38, height: 38, borderRadius: 10,
                          background: `linear-gradient(135deg, ${bucketCfg.accent}20 0%, ${bucketCfg.accent}08 100%)`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 18, color: bucketCfg.accent, fontWeight: 700,
                          border: `1px solid ${bucketCfg.accent}15`,
                          overflow: 'hidden', flexShrink: 0,
                        }}>
                          {(agent.icon && agent.icon.length <= 2) ? agent.icon : <bucketCfg.icon size={18} />}
                        </div>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <h3 style={{ fontSize: 15, fontWeight: 650, color: '#1c1917', margin: 0, letterSpacing: '-0.01em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {agent.name}
                          </h3>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3 }}>
                            <span style={{
                              // Natural-case label so the inline capital
                              // L mid-word stays visible — "AutoLets",
                              // "Lets", "ExteLets", "SysLets" all rely
                              // on that capital to read as a family.
                              // Uppercasing them turned the chip into
                              // "AUTOLETS" / "LETS" which lost the
                              // family signature and made "LETS" read
                              // as a verb.
                              fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 4,
                              background: `${bucketCfg.accent}14`, color: bucketCfg.accent,
                              border: `1px solid ${bucketCfg.accent}30`,
                              letterSpacing: 0.2,
                            }}>{bucketCfg.label}</span>
                            <span style={{ fontSize: 11, color: '#a8a29e' }}>{agent.type}</span>
                          </div>
                        </div>
                      </div>
                      <Badge status={agent.published ? 'active' : 'pending'} />
                    </div>

                    <div style={{
                      display: 'flex', gap: 16, fontSize: 12,
                      padding: '12px 14px', background: '#fafaf9', borderRadius: 10,
                    }}>
                      <div>
                        <div style={{ color: '#a8a29e', marginBottom: 3, fontSize: 10, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' }}>Skills</div>
                        <div style={{ fontWeight: 700, color: '#1c1917', fontSize: 16 }}>
                          {agent.skills?.length ?? 0}
                        </div>
                      </div>
                      <div style={{ width: 1, background: '#e7e5e4' }} />
                      <div>
                        <div style={{ color: '#a8a29e', marginBottom: 3, fontSize: 10, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' }}>Phase</div>
                        <div style={{
                          fontWeight: 650, color: pc.text, fontSize: 13,
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          padding: '2px 8px', borderRadius: 6, background: pc.bg,
                        }}>
                          {formatPhase(agent.phase)}
                        </div>
                      </div>
                      <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'flex-end' }}>
                        <span style={{
                          fontSize: 11, color: '#a8a29e', display: 'flex', alignItems: 'center', gap: 4,
                        }}>
                          Open <ArrowRight size={12} />
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <Pagination totalItems={agents.length} page={pgAgents.page} pageSize={pgAgents.pageSize} onPageChange={pgAgents.setPage} onPageSizeChange={pgAgents.setPageSize} noun="agents" />
        </>
      )}

      {/* Keyframe animation for the spinner */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(0.85); }
        }
      `}</style>
    </PageContainer>
  );
}

// ─────────────────────────────────────────────────────────────────────
// BucketTabBar — four-bucket toggle above the agents grid.
//
// Renders one chip per bucket with its live count + the bucket's accent
// colour. Selected chip uses the bucket's full gradient + border palette
// (matching the cards below) so the page reads as one coherent fleet
// view rather than a tab-and-grid pair.
// ─────────────────────────────────────────────────────────────────────
function BucketTabBar({ agents, selected, onSelect, lastRefreshed }) {
  // Single pass through `agents` to count per bucket.
  const counts = React.useMemo(() => {
    const c = { all: agents.length, autolets: 0, agentlet: 0, external: 0, system: 0 };
    for (const a of agents) c[classifyAgent(a)]++;
    return c;
  }, [agents]);

  const selectedCfg = AGENT_BUCKETS[selected] || AGENT_BUCKETS.all;
  const visibleCount = counts[selected] ?? agents.length;

  return (
    <>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 12,
      }}>
        <h2 style={{ fontSize: 17, fontWeight: 650, color: '#1c1917', margin: 0, letterSpacing: '-0.01em' }}>
          Agents
          <span style={{
            marginLeft: 8, fontSize: 12, fontWeight: 500, color: '#78716c',
            background: '#f5f5f4', padding: '2px 8px', borderRadius: 6,
          }}>
            {visibleCount} of {agents.length}
          </span>
        </h2>
        {lastRefreshed && (
          <span style={{ fontSize: 11, color: '#a8a29e' }}>
            Updated {lastRefreshed.toLocaleTimeString()}
          </span>
        )}
      </div>

      {/* Tab row — each chip is its own button so keyboard nav works. */}
      <div style={{
        display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap',
      }}>
        {BUCKET_ORDER.map((key) => {
          const cfg = AGENT_BUCKETS[key];
          const Icon = cfg.icon;
          const active = key === selected;
          const count = counts[key] ?? 0;
          return (
            <button
              key={key}
              type="button"
              onClick={() => onSelect(key)}
              title={cfg.description}
              style={{
                background: active ? cfg.bg : '#ffffff',
                border: `1px solid ${active ? cfg.border : '#e7e5e4'}`,
                borderRadius: 10, padding: '8px 14px',
                cursor: 'pointer', fontFamily: 'inherit',
                display: 'inline-flex', alignItems: 'center', gap: 8,
                color: active ? cfg.accent : '#52525b',
                fontWeight: active ? 650 : 500, fontSize: 13,
                boxShadow: active ? '0 2px 8px rgba(0,0,0,0.04)' : 'none',
                transition: 'all 0.15s ease',
              }}
            >
              <Icon size={14} style={{ color: active ? cfg.accent : '#a8a29e' }} />
              {cfg.label}
              <span style={{
                fontSize: 10, fontWeight: 700,
                padding: '1px 7px', borderRadius: 4,
                background: active ? `${cfg.accent}1a` : '#f5f5f4',
                color: active ? cfg.accent : '#78716c',
              }}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Bucket description strip — only shown when a specific bucket is
          selected, so "All" stays uncluttered. */}
      {selected !== 'all' && (
        <div style={{
          background: selectedCfg.bg, border: `1px solid ${selectedCfg.border}`,
          borderRadius: 10, padding: '10px 14px', fontSize: 12, color: '#52525b',
          marginBottom: 14, display: 'flex', gap: 10, alignItems: 'center',
        }}>
          <selectedCfg.icon size={14} style={{ color: selectedCfg.accent, flexShrink: 0 }} />
          <span><strong style={{ color: selectedCfg.accent }}>{selectedCfg.label}</strong> · {selectedCfg.description}</span>
        </div>
      )}
    </>
  );
}
